import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';

/** Top inset on PDF pages after the first (jsPDF px, matches A4 page units). */
const PDF_CONTINUATION_TOP_MARGIN_PX = 24;

export interface PageSlice {
    yOffset: number;
    sliceHeight: number;
}

export interface VerticalBand {
    top: number;
    bottom: number;
}

function rowIsMostlyWhite(
    srcCtx: CanvasRenderingContext2D,
    imgWidth: number,
    y: number
): boolean {
    const row = srcCtx.getImageData(0, y, imgWidth, 1).data;
    for (let x = 0; x < row.length; x += 4) {
        if (row[x] < 250 || row[x + 1] < 250 || row[x + 2] < 250) {
            return false;
        }
    }
    return true;
}

/** True if horizontal cut at cutY (first row of next page) intersects the interior of band. */
function cutSplitsBand(band: VerticalBand, cutY: number): boolean {
    return band.top < cutY && cutY < band.bottom;
}

/**
 * Leaf `.user-input` nodes (outermost wrappers that contain label + control), so nested
 * fields (e.g. height/weight) stay atomic per control without duplicating parent bands.
 */
function collectQuestionBlockBands(
    root: HTMLElement,
    imgHeight: number
): VerticalBand[] {
    const all = [...root.querySelectorAll('.user-input')] as HTMLElement[];
    if (all.length === 0) return [];

    const blocks = all.filter(
        (el) => !all.some((other) => other !== el && el.contains(other))
    );

    const rootRect = root.getBoundingClientRect();
    const sh = Math.max(root.scrollHeight, root.offsetHeight, 1);
    const scaleY = imgHeight / sh;

    return blocks
        .map((el) => {
            const r = el.getBoundingClientRect();
            const top = (r.top - rootRect.top + root.scrollTop) * scaleY;
            const bottom = (r.bottom - rootRect.top + root.scrollTop) * scaleY;
            return {
                top: Math.max(0, Math.floor(top)),
                bottom: Math.min(imgHeight, Math.ceil(bottom)),
            };
        })
        .filter((b) => b.bottom - b.top > 2);
}

function cutSplitsAnyProtectedBand(
    cutY: number,
    bands: VerticalBand[] | undefined,
    srcPageHeight: number
): boolean {
    if (!bands?.length) return false;
    return bands.some((b) => {
        if (b.bottom - b.top > srcPageHeight * 0.98) return false;
        return cutSplitsBand(b, cutY);
    });
}

/**
 * Scans an offscreen canvas image and returns page slices with safe break points,
 * avoiding cutting through content rows. Optional protected bands keep label+field blocks intact.
 * Continuation pages use a shorter max slice height so a top margin can be painted without
 * overflowing the PDF page.
 */
export function processPageBreaks(
    srcCtx: CanvasRenderingContext2D,
    imgWidth: number,
    imgHeight: number,
    srcPageHeightFirst: number,
    srcPageHeightContinuation: number,
    protectedBands?: VerticalBand[]
): PageSlice[] {
    const slices: PageSlice[] = [];
    let yOffset = 0;

    while (yOffset < imgHeight) {
        const maxSrcSlice =
            yOffset === 0 ? srcPageHeightFirst : srcPageHeightContinuation;
        let sliceHeight = Math.min(maxSrcSlice, imgHeight - yOffset);

        if (yOffset + sliceHeight < imgHeight) {
            const searchStart = Math.floor(yOffset + sliceHeight * 0.95);
            const searchEnd = Math.floor(yOffset + sliceHeight * 0.40);
            let safeCut = -1;

            for (let y = searchStart; y > searchEnd; y--) {
                if (!rowIsMostlyWhite(srcCtx, imgWidth, y)) continue;
                if (cutSplitsAnyProtectedBand(y, protectedBands, maxSrcSlice)) continue;
                safeCut = y;
                break;
            }

            if (safeCut > yOffset) {
                sliceHeight = safeCut - yOffset;
            }
        }

        slices.push({ yOffset, sliceHeight });
        yOffset += sliceHeight;
    }

    return slices;
}

/**
 * Generates a PDF from an HTML element and opens it in a new tab.
 *
 * @param element - The HTML element to capture as a PDF
 */
export async function generatePdf(element: HTMLElement): Promise<void> {
    const dataUrl = await toPng(element, { cacheBust: true });
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => {
        img.onload = () => resolve();
    });

    const srcCanvas = document.createElement('canvas');
    srcCanvas.width = img.width;
    srcCanvas.height = img.height;
    const srcCtx = srcCanvas.getContext('2d')!;
    srcCtx.fillStyle = '#ffffff';
    srcCtx.fillRect(0, 0, srcCanvas.width, srcCanvas.height);
    srcCtx.drawImage(img, 0, 0);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const scale = img.width / pageWidth;
    const srcPageHeightFirst = Math.floor(pageHeight * scale);
    const topMarginSrc = Math.min(
        Math.floor(PDF_CONTINUATION_TOP_MARGIN_PX * scale),
        Math.floor(srcPageHeightFirst * 0.35)
    );
    const srcPageHeightContinuation = Math.max(
        srcPageHeightFirst - topMarginSrc,
        Math.floor(srcPageHeightFirst * 0.55)
    );

    const protectedBands = collectQuestionBlockBands(element, img.height);
    const slices = processPageBreaks(
        srcCtx,
        img.width,
        img.height,
        srcPageHeightFirst,
        srcPageHeightContinuation,
        protectedBands
    );

    slices.forEach(({ yOffset, sliceHeight }, pageIndex) => {
        const topPadSrc = pageIndex === 0 ? 0 : topMarginSrc;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = img.width;
        pageCanvas.height = sliceHeight + topPadSrc;
        const pageCtx = pageCanvas.getContext('2d')!;
        pageCtx.fillStyle = '#ffffff';
        pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        pageCtx.drawImage(
            srcCanvas,
            0,
            yOffset,
            img.width,
            sliceHeight,
            0,
            topPadSrc,
            img.width,
            sliceHeight
        );

        const sliceDataUrl = pageCanvas.toDataURL('image/png');
        const slicePdfHeight = pageCanvas.height / scale;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(sliceDataUrl, 'PNG', 0, 0, pageWidth, slicePdfHeight);
    });

    const pdfOutput = pdf.output('bloburl');
    window.open(pdfOutput, '_blank');
}
